// Folders and tags, one level: a handout names its folder, '' is the root. Pure.
import type { Handout } from './types';

export interface Folder {
  name: string;
  handouts: Handout[];
}

export type HandoutSort = 'newest' | 'largest' | 'title';

const ORDER: Record<HandoutSort, (a: Handout, b: Handout) => number> = {
  newest: (a, b) => b.createdAt - a.createdAt || a.title.localeCompare(b.title),
  largest: (a, b) => b.size - a.size || b.createdAt - a.createdAt,
  title: (a, b) => a.title.localeCompare(b.title) || b.createdAt - a.createdAt,
};

/**
 * Root first, then folders by name; inside, newest first unless asked
 * otherwise (largest first is how the GM finds what to delete). A tag
 * filter keeps only what carries every tag.
 */
export function folderTree(
  handouts: readonly Handout[],
  tagFilter: readonly string[] = [],
  sort: HandoutSort = 'newest',
): Folder[] {
  const kept = tagFilter.length ? handouts.filter((h) => tagFilter.every((t) => h.tags.includes(t))) : [...handouts];
  const by = new Map<string, Handout[]>();
  for (const h of kept) (by.get(h.folder) ?? by.set(h.folder, []).get(h.folder)!).push(h);
  const names = [...by.keys()].sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));
  return names.map((name) => ({ name, handouts: by.get(name)!.sort(ORDER[sort]) }));
}

export function allTags(handouts: readonly Handout[]): string[] {
  return [...new Set(handouts.flatMap((h) => h.tags))].sort();
}
